import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";
import { BRAND } from "../mock/mock";
import { api } from "../lib/api";
import { PHOTOS } from "../data/photos";
import { LOCALITIES_SEO } from "../data/seoContent";

const QUICK_LINKS = [
  { to: "/tests", label: "Tests" },
  { to: "/health-packages", label: "Health Packages" },
  { to: "/home-sample-collection-ranchi", label: "Home Sample Collection" },
  { to: "/locations", label: "Locations" },
  { to: "/about-doctorlab", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
];

const PILLAR_LINKS = [
  { to: "/pathology-lab-ranchi", label: "Pathology lab in Ranchi" },
  { to: "/diagnostic-centre-ranchi", label: "Diagnostic centre in Ranchi" },
  { to: "/blood-test-ranchi", label: "Blood test in Ranchi" },
  { to: "/home-sample-collection-ranchi", label: "Home sample collection" },
  { to: "/health-checkup-ranchi", label: "Health checkup in Ranchi" },
];

const Footer = () => {
  const [brand, setBrand] = useState(BRAND);
  useEffect(() => { api.getBrand(BRAND).then((b) => b && setBrand({ ...BRAND, ...b })); }, []);
  const waNumber = (brand.whatsapp || brand.phone || "").replace(/\D/g, "");
  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="container-x py-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-2.5 mb-4">
            <img src={PHOTOS.logo.src} alt={PHOTOS.logo.alt} width={48} height={48} loading="lazy" className="h-12 w-12 rounded-full object-cover ring-2 ring-white/10" />
            <div>
              <div className="text-white font-bold text-lg">DoctorLab &amp; Diagnostics</div>
              <div className="text-xs text-teal-300/80 uppercase tracking-widest font-semibold">Pathology Lab in Ranchi</div>
            </div>
          </div>
          <p className="text-sm text-slate-400 leading-relaxed max-w-md">
            DoctorLab &amp; Diagnostics is a pathology laboratory and diagnostic centre serving patients in Ranchi, Jharkhand. We provide laboratory investigations, blood tests, preventive health testing and sample collection services. Contact DoctorLab for test availability, booking and home collection information.
          </p>

          <div className="mt-6 space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-dl-teal shrink-0" />
              <a data-testid="footer-phone-primary" href={`tel:${brand.phone}`} className="hover:text-white">{brand.phone}</a>
              {brand.phone2 && (
                <>
                  <span className="text-slate-600">·</span>
                  <a data-testid="footer-phone-secondary" href={`tel:${brand.phone2}`} className="hover:text-white">{brand.phone2}</a>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-dl-teal shrink-0" />
              <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noreferrer" className="hover:text-white">WhatsApp {brand.whatsapp}</a>
            </div>
            <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-dl-teal shrink-0" /> <a href={`mailto:${brand.email}`} className="hover:text-white">{brand.email}</a></div>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-dl-teal mt-0.5 shrink-0" /> <span>Annapurna Chowk, Lalpur, Ranchi, Jharkhand – 834001</span></div>
            <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-dl-teal shrink-0" /> <span>{brand.hours || "Mon–Sun · 7:30 AM – 9:00 PM"}</span></div>
          </div>

          <div className="flex gap-2 mt-6">
            <a href="https://share.google/2xoCLmkXAmpzk63ys" target="_blank" rel="noreferrer" aria-label="Google Business Profile" className="h-9 px-3 rounded-full bg-slate-800 hover:bg-dl-blue flex items-center gap-2 transition text-xs font-semibold text-white">
              <MapPin className="h-4 w-4" /> Google Business
            </a>
            <a
              href="https://www.facebook.com/share/19P75uLQfR/?mibextid=wwXIfr"
              target="_blank"
              rel="noreferrer"
              aria-label="DoctorLab on Facebook"
              className="h-9 w-9 rounded-full bg-slate-800 hover:bg-[#1877F2] flex items-center justify-center transition"
            >
              <Facebook className="h-4 w-4" />
            </a>
            <a href="#" aria-label="Instagram (coming soon)" className="h-9 w-9 rounded-full bg-slate-800 hover:bg-dl-blue flex items-center justify-center transition opacity-50 pointer-events-none"><Instagram className="h-4 w-4" /></a>
          </div>
        </div>

        <div>
          <div className="text-white font-semibold mb-4 text-sm">Quick Links</div>
          <ul className="space-y-2 text-sm text-slate-400">
            {QUICK_LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-white font-semibold mb-4 text-sm">Popular Services</div>
          <ul className="space-y-2 text-sm text-slate-400">
            {PILLAR_LINKS.map((l) => (
              <li key={l.to}><Link to={l.to} className="hover:text-white">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        <div>
          <div className="text-white font-semibold mb-4 text-sm">Ranchi Localities</div>
          <ul className="space-y-2 text-sm text-slate-400">
            {LOCALITIES_SEO.map((l) => (
              <li key={l.slug}><Link to={`/locations/${l.slug}`} className="hover:text-white">Pathology lab near {l.locality}</Link></li>
            ))}
          </ul>
        </div>
      </div>

      <div className="border-t border-slate-800">
        <div className="container-x py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} DoctorLab &amp; Diagnostics. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/faq" className="hover:text-white">FAQ</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
            <a href="/sitemap.xml" className="hover:text-white">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
